import React, { useEffect, useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { apiUrl } from '../utils/apiBaseUrl';

const LOCKOUT_SECONDS = 30;

const normalizeEmail = (value) => String(value || '').trim().toLowerCase();

const normalizeRole = (value) => {
  const role = String(value || '').trim().toLowerCase();
  if (role === 'superadmin') return 'super_admin';
  return role;
};

const isAdminRole = (value) => {
  const normalized = normalizeRole(value);
  return normalized === 'admin' || normalized === 'super_admin';
};

const resolveRoleFromMetadata = (user) => {
  const appMetadata = user?.app_metadata || {};
  const userMetadata = user?.user_metadata || {};

  const directCandidates = [appMetadata.role, userMetadata.role];
  for (const candidate of directCandidates) {
    if (isAdminRole(candidate)) return normalizeRole(candidate);
  }

  const listCandidates = [appMetadata.roles, userMetadata.roles];
  for (const listValue of listCandidates) {
    if (!Array.isArray(listValue)) continue;
    const hit = listValue.find((item) => isAdminRole(item));
    if (hit) return normalizeRole(hit);
  }

  return null;
};

const getSecondsRemaining = (payload) => {
  const seconds = Number(payload?.seconds_remaining);
  return Number.isFinite(seconds) && seconds > 0 ? Math.ceil(seconds) : LOCKOUT_SECONDS;
};

const AdminLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [lockoutSecondsLeft, setLockoutSecondsLeft] = useState(0);
  const [lockoutEmail, setLockoutEmail] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    if (lockoutSecondsLeft <= 0) return undefined;

    const timer = window.setInterval(() => {
      setLockoutSecondsLeft((prev) => {
        if (prev <= 1) {
          window.clearInterval(timer);
          return 0;
        }

        return prev - 1;
      });
    }, 1000);

    return () => window.clearInterval(timer);
  }, [lockoutSecondsLeft]);

  const startLockoutCountdown = (seconds, currentEmail) => {
    const normalizedSeconds = Math.max(0, Math.ceil(Number(seconds) || 0));
    if (!normalizedSeconds) return;

    setLockoutSecondsLeft(normalizedSeconds);
    setLockoutEmail(normalizeEmail(currentEmail));
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const normalizedEmail = normalizeEmail(email);

    if (lockoutSecondsLeft > 0 && normalizedEmail === lockoutEmail) {
      setError(`Too many failed attempts. Try again in ${lockoutSecondsLeft}s.`);
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(apiUrl('/auth/admin-login/'), {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          email: normalizedEmail,
          password,
        }),
      });

      const contentType = response.headers.get('content-type') || '';
      const rawBody = await response.text();

      let payload = null;
      if (rawBody) {
        if (contentType.includes('application/json')) {
          try {
            payload = JSON.parse(rawBody);
          } catch {
            payload = null;
          }
        } else {
          // Some proxies/middlewares may strip content-type; attempt JSON parse as a fallback.
          try {
            payload = JSON.parse(rawBody);
          } catch {
            payload = null;
          }
        }
      }

      if (!payload) {
        const bodyPreview = (rawBody || '').trim().slice(0, 120).toLowerCase();
        const looksLikeHtml = bodyPreview.startsWith('<!doctype html') || bodyPreview.startsWith('<html');

        if (looksLikeHtml) {
          throw new Error(
            `Admin login API returned HTML instead of JSON (HTTP ${response.status}). Verify REACT_APP_API_BASE_URL points to your backend origin (or /api), not a frontend page.`
          );
        }

        throw new Error(`Admin login endpoint returned an unreadable response (HTTP ${response.status}).`);
      }

      if (!response.ok) {
        if (payload?.result === 'locked_out') {
          const secondsRemaining = getSecondsRemaining(payload);
          startLockoutCountdown(secondsRemaining, normalizedEmail);
          setError(`Too many failed attempts. Try again in ${secondsRemaining}s.`);
          return;
        }

        if (payload?.result === 'inactive') {
          setError(payload?.detail || 'Account is inactive. Contact support.');
          return;
        }

        if (payload?.result === 'forbidden') {
          setError(payload?.detail || 'Access Denied: You do not have admin privileges.');
          return;
        }

        if (payload?.result === 'invalid_credentials') {
          const attemptsRemaining = Number(payload?.attempts_remaining ?? 0);
          setError(`Incorrect email or password. ${attemptsRemaining} attempt(s) remaining.`);
          return;
        }

        throw new Error(payload?.detail || 'Login failed.');
      }

      let supabaseUser = null;
      if (payload?.access_token && payload?.refresh_token) {
        const { data: sessionData, error: sessionError } = await supabase.auth.setSession({
          access_token: payload.access_token,
          refresh_token: payload.refresh_token,
        });
        if (sessionError || !sessionData?.user) {
          throw new Error(
            'Backend login succeeded, but Supabase session could not be established. Please verify this admin account exists in Supabase and try again.'
          );
        }
        supabaseUser = sessionData.user;
      } else {
        const { data: signInData, error: supabaseSignInError } = await supabase.auth.signInWithPassword({
          email: normalizedEmail,
          password,
        });

        if (supabaseSignInError || !signInData?.user) {
          throw new Error(
            'Backend login succeeded, but Supabase session could not be established. Please verify this admin account exists in Supabase and try again.'
          );
        }
        supabaseUser = signInData.user;
      }

      const metadataRole = resolveRoleFromMetadata(supabaseUser);

      if (!metadataRole) {
        const { data: existingProfile, error: profileReadError } = await supabase
          .from('users')
          .select('id, role')
          .eq('id', supabaseUser.id)
          .maybeSingle();

        if (profileReadError) {
          throw new Error('Unable to read Supabase admin profile. Please check RLS policies for users table.');
        }

        if (!existingProfile || !isAdminRole(existingProfile.role)) {
          await supabase.auth.signOut();
          localStorage.removeItem('isAdminAuthenticated');
          localStorage.removeItem('adminRole');
          throw new Error('Supabase account is missing admin role mapping. Please set role to admin/super_admin in users table or metadata.');
        }
      }

      localStorage.setItem('isAdminAuthenticated', 'true');
      localStorage.setItem('adminRole', payload?.user?.role || 'admin');
      setLockoutSecondsLeft(0);
      setLockoutEmail('');
      setError('');
      navigate('/admin/dashboard');
    } catch (loginError) {
      const message = loginError?.message || '';
      const normalizedMessage = String(message).toLowerCase();

      if (normalizedMessage.includes('failed to fetch') || normalizedMessage.includes('networkerror')) {
        setError('Cannot reach login API. Verify REACT_APP_API_BASE_URL points to your backend and that CORS is enabled.');
      } else {
        setError(message || 'Login failed. Please try again.');
      }

      console.error('Login Error:', message);
    } finally {
      setLoading(false);
    }
  };

  const isLockedForCurrentEmail = lockoutSecondsLeft > 0 && normalizeEmail(email) === lockoutEmail;

  return (
    <div className="min-h-screen flex font-sans bg-[#f3f2f1]">
      <div className="hidden md:flex w-1/2 bg-black items-center justify-center">
        <div className="flex flex-col items-center">
          <img
            src="/assets/echojham.logo3.png"
            alt="Logo"
            className="w-64 mb-3"
            onError={(event) => {
              event.target.src = 'https://via.placeholder.com/250?text=Echo+Jham';
            }}
          />
        </div>
      </div>

      <div className="w-full md:w-1/2 bg-[#f3f2f1] flex items-center justify-center py-10">
        <form onSubmit={handleLogin} className="w-full max-w-sm text-center px-8">
          <h2 className="text-xl font-bold text-[#7b0000] mb-6 tracking-widest uppercase italic">
            Admin Portal
          </h2>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 text-red-700 text-xs font-bold rounded-r-xl flex items-center gap-3 text-left">
              <span className="text-base leading-none">!</span>
              <span>{error}</span>
            </div>
          )}

          <div className="space-y-4">
            <input
              type="email"
              placeholder="Admin Email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              className="w-full px-4 py-3 rounded-md border border-gray-300 text-black text-sm focus:outline-none focus:ring-2 focus:ring-[#7b0000] bg-white"
              required
            />

            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder="Password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                className="w-full px-4 pr-12 py-3 rounded-md border border-gray-300 text-black text-sm focus:outline-none focus:ring-2 focus:ring-[#7b0000] bg-white"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword((prev) => !prev)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-[#7b0000] p-1 min-h-10 min-w-10 flex items-center justify-center"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>

            <button
              type="submit"
              disabled={loading || isLockedForCurrentEmail}
              className={`w-full sm:w-40 mx-auto py-3 rounded-md text-sm font-bold transition shadow-lg uppercase tracking-widest active:scale-95 text-white ${
                loading || isLockedForCurrentEmail
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-[#7b0000] hover:bg-red-800'
              }`}
            >
              {loading
                ? 'Verifying...'
                : isLockedForCurrentEmail
                  ? `Locked (${lockoutSecondsLeft}s)`
                  : 'Login'}
            </button>
          </div>

          <p className="mt-8 text-[10px] text-gray-400 uppercase tracking-widest italic">
            Authorized Personnel Only
          </p>
        </form>
      </div>
    </div>
  );
};

export default AdminLogin;